import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PDFViewer } from '@/components/ui/PDFViewer';
import { attachmentService, type Attachment } from '@/services';

interface QuoteSourceDocumentsProps {
  versionId: string | null;
  isVisible: boolean;
}

interface AttachmentWithUrl extends Attachment {
  signedUrl: string | null;
}

export function QuoteSourceDocuments({ versionId, isVisible }: QuoteSourceDocumentsProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [attachmentsWithUrls, setAttachmentsWithUrls] = useState<AttachmentWithUrl[]>([]);

  // Query attachments for the version
  const { data: attachments = [], isLoading, error } = useQuery({
    queryKey: ['quote-version-attachments', versionId],
    queryFn: async () => {
      if (!versionId) return [];
      const result = await attachmentService.listByEntity('quote_version', versionId);
      if (result.error) throw result.error;
      // Filter for import source attachments only
      return result.data.filter(a => a.attachment_role === 'import source');
    },
    enabled: !!versionId && isVisible,
  });

  // Fetch signed URLs for all attachments
  useEffect(() => {
    const fetchSignedUrls = async () => {
      if (!attachments.length) {
        setAttachmentsWithUrls([]);
        return;
      }

      const withUrls = await Promise.all(
        attachments.map(async (attachment) => {
          const { url, error } = await attachmentService.getSignedUrl(attachment.file_path);
          if (error) {
            console.error('Failed to get signed URL for:', attachment.file_path, error);
          } else {
            console.log('Signed URL generated for:', attachment.file_name, url);
          }
          return {
            ...attachment,
            signedUrl: error ? null : url,
          };
        })
      );

      setAttachmentsWithUrls(withUrls);
    };

    fetchSignedUrls();
  }, [attachments]);

  // Reset selected index when attachments change
  useEffect(() => {
    setSelectedIndex(0);
  }, [versionId]);

  if (!isVisible || !versionId) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <Card className="border rounded-lg bg-card h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading source documents...</p>
        </div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="border rounded-lg bg-card h-full p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load source documents: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  // Empty state
  if (attachmentsWithUrls.length === 0) {
    return (
      <Card className="border rounded-lg bg-card h-full flex items-center justify-center p-6">
        <div className="flex flex-col items-center text-muted-foreground">
          <FileText className="w-12 h-12 mb-2" />
          <p className="text-sm">No source documents found</p>
        </div>
      </Card>
    );
  }

  // Single attachment - no tabs needed
  if (attachmentsWithUrls.length === 1) {
    const attachment = attachmentsWithUrls[0];
    return (
      <Card className="border rounded-lg bg-card h-full flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Source Documents</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{attachment.file_name}</p>
        </div>
        <div className="flex-1 overflow-auto">
          <AttachmentViewer attachment={attachment} />
        </div>
      </Card>
    );
  }

  // Multiple attachments - use tabs
  return (
    <Card className="border rounded-lg bg-card h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Source Documents</h3>
        </div>
      </div>
      <Tabs
        value={String(selectedIndex)}
        onValueChange={(v) => setSelectedIndex(Number(v))}
        className="flex-1 flex flex-col"
      >
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0">
          {attachmentsWithUrls.map((attachment, index) => (
            <TabsTrigger
              key={attachment.id}
              value={String(index)}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 text-sm"
            >
              {attachment.file_name}
            </TabsTrigger>
          ))}
        </TabsList>
        {attachmentsWithUrls.map((attachment, index) => (
          <TabsContent
            key={attachment.id}
            value={String(index)}
            className="flex-1 overflow-auto mt-0"
          >
            <AttachmentViewer attachment={attachment} />
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
}

// Helper component to render plain text files
function TextFileViewer({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch file (${res.status})`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) {
          setContent(text);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setFetchError(err instanceof Error ? err.message : 'Failed to load file');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [url]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 h-full">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-muted-foreground h-full">
        <AlertCircle className="w-12 h-12 mb-2" />
        <p className="text-sm">{fetchError}</p>
      </div>
    );
  }

  return (
    <pre className="p-4 text-sm whitespace-pre-wrap break-words overflow-auto h-full font-mono">
      {content}
    </pre>
  );
}

// Helper component to render individual attachment
function AttachmentViewer({ attachment }: { attachment: AttachmentWithUrl }) {
  // Check if signed URL is available
  if (!attachment.signedUrl) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-muted-foreground h-full">
        <AlertCircle className="w-12 h-12 mb-2" />
        <p className="text-sm">Failed to load file</p>
        <p className="text-xs mt-1">Could not generate download URL</p>
      </div>
    );
  }

  // Determine file type
  const isPdf =
    attachment.mime_type === 'application/pdf' ||
    attachment.file_name.toLowerCase().endsWith('.pdf');

  const isXlsx =
    attachment.mime_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    attachment.mime_type === 'application/vnd.ms-excel' ||
    attachment.file_name.toLowerCase().endsWith('.xlsx') ||
    attachment.file_name.toLowerCase().endsWith('.xls');

  // Render PDF viewer
  if (isPdf) {
    return <PDFViewer url={attachment.signedUrl} className="h-full" />;
  }

  // Detect text files
  const isText =
    attachment.mime_type === 'text/plain' ||
    attachment.file_name.toLowerCase().endsWith('.txt');

  // Render text viewer
  if (isText) {
    return <TextFileViewer url={attachment.signedUrl} />;
  }

  // Render XLSX placeholder
  if (isXlsx) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-muted-foreground h-full">
        <FileSpreadsheet className="w-12 h-12 mb-2" />
        <p className="text-sm">XLSX preview</p>
        <p className="text-sm">coming soon.</p>
      </div>
    );
  }

  // Unsupported file type
  return (
    <div className="flex flex-col items-center justify-center p-8 text-muted-foreground h-full">
      <AlertCircle className="w-12 h-12 mb-2" />
      <p className="text-sm">Unsupported file type</p>
      <p className="text-xs mt-1">Only PDF and Excel (.xlsx) files are supported</p>
    </div>
  );
}
